// PB-G0 — Powerball pipeline integrity: locked ranges (UI side; backend mirror at supabase/functions/_shared/powerball-ranges.ts).
// Both files MUST stay in sync. Tests in src/powerball/__tests__/ranges.test.ts assert agreement.

// FAVOURITES MODE (founder, 2026-06-19) — see _shared/powerball-ranges.ts. Lower
// combined-odds targets so PB10/MEGA25 field short favourites, not coin-flips.
// Raw tiers rank toward short favourites but may use independently supported
// reserve prices up to 2.65 when exact cross-tier fixture exclusivity needs
// more depth. Target-aware ranking still consumes shorter favourites first.
export const PB_LEG_MAX_ODDS = 2.65;
export const PB10_ODDS_MIN = 40;
export const PB10_ODDS_MAX = 1000;
export const MEGA20_ODDS_MIN = 2000;
export const MEGA20_ODDS_MAX = 40000;

// MIXED-MEGA — exactly 45 mixed-market legs. The assembler maximises league
// breadth first, then permits one second fixture per league when fewer than 45
// leagues are available. The narrower combined band keeps the long accumulator
// on short, high-conviction legs.
export const MIXED_MEGA_ODDS_MIN = 700_000;
export const MIXED_MEGA_ODDS_MAX = 40_000_000_000_000;

// LOWER LINES — targets 50 same-day legs; weekdays may finish at 30-40.
export const LOWER_LINES_ODDS_MIN = 9_100;
export const LOWER_LINES_ODDS_MAX = 20_250_000;

// GOALS BANK — exactly 40 goals-market legs, each priced at 1.27-1.40.
export const GOALS_BANK_ODDS_MIN = 14_195;
export const GOALS_BANK_ODDS_MAX = 700_038;

// THE BIG STAGE — a variable 18-40 leg UEFA matchweek ticket.
export const BIG_STAGE_ODDS_MIN = 100;
export const BIG_STAGE_ODDS_MAX = 20_000_000_000;

// 2026-05-20 — Foundation floor lowered from 1.60 to 1.15 to support
// MEGA50 (50-leg variant of mega_powerball_20). With 50 legs targeting
// combined 50K-500K the per-leg geomean lands at ~1.17-1.22; a 1.60
// floor would make the slip un-assemblable. PB10 is unaffected — its
// target geomean (~2.0) means the assembler still picks higher.
export const FOUNDATION_MIN = 1.15;
export const FOUNDATION_MAX = 2.2;
export const MID_LAYER_MIN = 2.2;
export const MID_LAYER_MAX = 3.5;
export const AMPLIFIER_MIN = 3.5;
export const AMPLIFIER_MAX = 5.0;

export type SlipType =
  | "powerball_10"
  | "mega_powerball_20"
  | "mixed_mega_jackpot"
  | "lower_lines"
  | "goals_bank"
  | "big_stage"
  | "multiverse";
export type LayerBand = "foundation" | "mid_layer" | "amplifier" | "unassigned";

export function getLayerForOdds(odds: number): LayerBand {
  if (!Number.isFinite(odds)) return "unassigned";
  if (odds > PB_LEG_MAX_ODDS) return "unassigned";
  if (odds >= FOUNDATION_MIN && odds < FOUNDATION_MAX) return "foundation";
  if (odds >= MID_LAYER_MIN && odds < MID_LAYER_MAX) return "mid_layer";
  if (odds >= AMPLIFIER_MIN && odds <= AMPLIFIER_MAX) return "amplifier";
  return "unassigned";
}

export function getOddsRangeForSlip(slipType: SlipType): { min: number; max: number } {
  switch (slipType) {
    case "powerball_10":         return { min: PB10_ODDS_MIN,        max: PB10_ODDS_MAX };
    case "mega_powerball_20":    return { min: MEGA20_ODDS_MIN,      max: MEGA20_ODDS_MAX };
    case "mixed_mega_jackpot":   return { min: MIXED_MEGA_ODDS_MIN,  max: MIXED_MEGA_ODDS_MAX };
    case "lower_lines":          return { min: LOWER_LINES_ODDS_MIN, max: LOWER_LINES_ODDS_MAX };
    case "goals_bank":           return { min: GOALS_BANK_ODDS_MIN,  max: GOALS_BANK_ODDS_MAX };
    case "big_stage":            return { min: BIG_STAGE_ODDS_MIN,   max: BIG_STAGE_ODDS_MAX };
    case "multiverse":           return { min: 1, max: Number.POSITIVE_INFINITY };
  }
}

export type OddsRangeStatus = "in_range" | "below_range" | "above_range";

export function checkOddsRange(slipType: SlipType, combinedOdds: number): OddsRangeStatus {
  const { min, max } = getOddsRangeForSlip(slipType);
  if (combinedOdds < min) return "below_range";
  if ((slipType === "mixed_mega_jackpot" || slipType === "lower_lines" || slipType === "goals_bank" || slipType === "big_stage") && combinedOdds > max) return "above_range";
  return "in_range";
}

export function getRequiredLegCount(slipType: SlipType): number {
  switch (slipType) {
    case "powerball_10":        return 10;
    case "mega_powerball_20":   return 25;
    case "mixed_mega_jackpot":  return 45;
    case "lower_lines":         return 50;
    case "goals_bank":          return 40;
    case "big_stage":           return 18;
    case "multiverse":          return 45;
  }
}

// PB-G1 — Tiered value-edge thresholds by layer.
// Foundation legs require the same positive-edge floor as the independent
// prediction-quality gate. Higher-variance legacy layers remain stricter.
// Amplifier legs (~3.50–5.00) carry high variance, so we demand a larger edge.
// Used by the pipeline's non-CLV fallback filter; CLV-enriched picks bypass.
export function requiredValueEdge(layer: LayerBand): number {
  switch (layer) {
    case "foundation":
      return 0.5;
    case "mid_layer":
      return 0.5;
    case "amplifier":
      return 15;
    case "unassigned":
    default:
      return Number.POSITIVE_INFINITY;
  }
}
